import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { runDiagnostics } from "../../api/diagnosticsApi";
import { DiagnosticsResponse } from "../../types/diagnostics";
import AvatarIndicator from "../../components/AvatarIndicator";
import Spinner from "../../components/Spinner";
import { Search, AlertCircle, Lightbulb, Megaphone, Shield, Sparkles, Gauge } from "lucide-react";

export default function WebsiteDiagnosticTool() {
  const location = useLocation(); // Get location object
  const [data, setData] = useState<DiagnosticsResponse | null>(null);
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Prepare data for the circular chart dynamically from category scores
  const scoreData = data ? data.categoryScores.map((cat, i) => ({
    name: cat.category,
    value: cat.percentage,
    // Using the color palette defined in the project's existing components
    color: ["#4f46e5", "#db2777", "#10b981", "#ea580c"][i % 4],
    icon: [Sparkles, Megaphone, Gauge, Shield][i % 4]
  })) : [];

  const handleRun = async () => {
    await executeDiagnostics(url);
  };

  // New function to handle running diagnostics, can be called with an explicit URL
  const executeDiagnostics = useCallback(async (targetUrl: string) => {
    if (!targetUrl.trim()) {
      setError("Please enter a valid URL");
      return;
    }

    // Basic URL validation
    try {
      new URL(targetUrl);
    } catch (_) {
      // If it's not a valid URL, check if it's a GUID (for QR code ID)
      // For now, just show error if not a valid URL format
      setError("Please enter a valid URL format (e.g., https://example.com)");
      return;
    }

    setLoading(true);
    setError(null);
    setData(null);

    try {
      const result = await runDiagnostics(targetUrl);
      setData(result);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to run diagnostics. Please try again."
      );
      console.error("Diagnostics error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Effect to read URL from query parameter on component mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const initialUrl = params.get('url');
    if (initialUrl) {
      const decodedUrl = decodeURIComponent(initialUrl);
      setUrl(decodedUrl);
      // Automatically run diagnostics if a URL is provided in the query param
      executeDiagnostics(decodedUrl);
    }
  }, [location.search, executeDiagnostics]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleRun();
    }
  };

  return (
    <div className="min-h-screen pt-10 p-6 md:p-8 lg:p-10 md:ml-0">
      {/* Header Section */}
      <header className="mt-5 mb-8 flex items-center justify-between animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2 leading-tight mb-0">
            <Search className="text-indigo-600" size={24} />
            Website Diagnostics
          </h1>
          <p className="text-slate-500 text-sm mt-1 text-left">
            Analyze your website's performance, SEO, and health metrics.
          </p>
        </div>
      </header>

      {/* Input Section */}
      <div className="mb-8 animate-fade-in" style={{ animationDelay: "100ms" }}>
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
          <label className="block text-sm font-semibold text-slate-900 mb-3">
            Enter Website URL
          </label>
          <div className="flex gap-3 flex-col sm:flex-row">
            <div className="flex-1 relative">
              <Search
                size={20}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 pointer-events-none"
              />
              <input
                type="text"
                placeholder="https://example.com"
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  if (error) setError(null);
                }}
                onKeyPress={handleKeyPress}
                disabled={loading}
                className="input-base pl-10 focus:ring-offset-0"
              />
            </div>
            <button
              onClick={handleRun}
              disabled={loading || !url.trim()}
              className="btn-primary btn-md shrink-0"
            >
              {loading ? (
                <>
                  <Spinner size="h-4 w-4" className="mr-2" />
                  Running...
                </>
              ) : (
                <>
                  <Search size={16} className="mr-2" />
                  Run Diagnostics
                </>
              )}
            </button>
          </div>
          {error && (
            <div className="mt-3 alert alert-error animate-slide-in-left">
              <AlertCircle size={20} className="flex-shrink-0" />
              <div>
                <p className="font-semibold text-left">Error</p>
                <p className="text-sm">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-xl leading-none hover:opacity-70"
              >
                ×
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm flex items-center justify-center z-50 rounded-lg">
          <div className="bg-white rounded-lg p-8 shadow-xl text-center animate-fade-in">
            <div className="mb-4 flex justify-center">
              <div className="relative">
                <Spinner size="h-12 w-12" />
                <div className="absolute inset-0 animate-pulse"></div>
              </div>
            </div>
            <p className="text-slate-900 font-semibold mb-1">Analyzing Website</p>
            <p className="text-slate-600 text-sm">This may take a moment...</p>
          </div>
        </div>
      )}

      {/* Results Section */}
      {data && !loading && (
        <div className="space-y-8 animate-fade-in">
          {/* 1. Top Section: Hero Bento Grid & Activity Rings */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Main Visualizer Card */}
            <div className="lg:col-span-5 bg-white rounded-3xl border border-slate-200 shadow-sm p-8 flex flex-row items-center justify-center gap-8 relative overflow-hidden group min-h-[300px]">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500"></div>
              
              {/* Left Side: Avatar */}
              <div className="flex-shrink-0">
                <AvatarIndicator state={data.visualIndicator} />
              </div>

              {/* Right Side: Score Result */}
              <div className="flex flex-col items-center md:items-start">
                <div className="bg-slate-50/50 backdrop-blur-sm px-8 py-6 rounded-[2.5rem] border border-slate-100 shadow-inner transition-all group-hover:bg-white group-hover:shadow-md">
                    <span className="text-7xl font-black text-slate-900 tracking-tighter leading-none block">
                      {data.overallPercentage}<span className="text-3xl text-indigo-600 font-bold ml-1">%</span>
                    </span>
                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-3 block">
                      Overall Health
                    </span>
                </div>
              </div>
            </div>

            {/* Metric Tiles */}
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {scoreData.map((s) => (
                <div key={s.name} className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 flex flex-col justify-between hover:border-blue-200 transition-all group">
                  <div className="flex justify-between items-start mb-4">
                    <div className="p-3 rounded-2xl bg-slate-50 group-hover:bg-white transition-colors border border-transparent group-hover:border-slate-100" style={{ color: s.color }}>
                      <s.icon size={24} />
                    </div>
                    <span className="text-4xl font-black tracking-tighter" style={{ color: s.color }}>{s.value}%</span>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-800 uppercase tracking-tight mb-2">{s.name}</h4>
                    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${s.value}%`, backgroundColor: s.color }}></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 2. Key Improvement Opportunities */}
          <div className="bg-indigo-50/50 rounded-3xl border border-indigo-100 p-8 shadow-sm">
            <h4 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <div className="p-2 bg-indigo-600 rounded-lg text-white">
                <Lightbulb size={20} />
              </div>
              Key Improvement Opportunities
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.recommendations.map((rec, i) => (
                <div key={i} className="flex items-start gap-4 p-5 bg-white rounded-2xl border border-indigo-50 shadow-sm">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-sm">
                    {i + 1}
                  </span>
                  {/* <p className="text-slate-700 text-sm font-medium leading-relaxed text-left">{rec}</p> */}
                  <p
                    className="text-slate-700 text-sm font-normal leading-relaxed text-left"
                    dangerouslySetInnerHTML={{ __html: rec }}
                  ></p>
                </div>
              ))}
            </div>
          </div>

          {/* 3. Mid CTA Section */}
          <div className="relative bg-gradient-to-br from-blue-600 to-indigo-800 rounded-[2.5rem] p-12 text-white text-center shadow-2xl overflow-hidden group">
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 pointer-events-none"></div>
            <div className="relative z-10">
              <h3 className="text-3xl font-bold mb-4 tracking-tight">Ready to Take Your Website Further?</h3>
              <p className="text-blue-100 text-lg leading-relaxed max-w-2xl mx-auto mb-8 font-normal">
                Whether you want a faster website, a stronger branding, or a smoother customer experience, 
                we're here to help turn insights into real improvements.
              </p>
              <button className="px-10 py-4 bg-white text-blue-700 font-bold rounded-full hover:shadow-xl hover:scale-105 transition-all active:scale-95">
                Contact Us
              </button>
            </div>
          </div>

          {/* 4. Report Summary Section */}
          <div className="bg-white rounded-3xl border border-slate-200 p-10 shadow-sm relative">
            <h3 className="text-2xl font-bold text-slate-900 mb-8 flex items-center gap-3">
              <Megaphone size={22} className="text-indigo-600" />
              Report Summary
            </h3>
            <div className="space-y-6 text-left border-l-4 border-indigo-100 pl-8 ml-2">
              {data.summaryReport.map((para, i) => (
                <p
                  key={i}
                  className="text-slate-600 leading-relaxed text-md font-normal"
                  dangerouslySetInnerHTML={{ __html: para }}
                />
              ))}
            </div>
          </div>

          {/* 5. Unlock Full Report CTA */}
          <div className="bg-slate-950 rounded-[2.5rem] border-none shadow-2xl p-12 text-white text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 bg-blue-600/20 rounded-full blur-[100px]"></div>
            <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-80 h-80 bg-indigo-600/10 rounded-full blur-[100px]"></div>
            <div className="relative z-10">
              {/* <div className="inline-block px-4 py-1.5 rounded-full bg-blue-600/20 border border-blue-500/30 text-blue-400 text-xs font-black uppercase tracking-widest mb-6">
                Premium Report
              </div> */}
              <h3 className="text-3xl font-bold mb-4">Unlock the Full Website Health Report</h3>
              <p className="text-slate-400 leading-relaxed max-w-2xl mx-auto mb-10 text-lg">
                Get the complete breakdown with detailed findings, improvement recommendations, and actionable insights.
              </p>
              <button className="px-12 py-5 bg-blue-600 text-white font-bold rounded-full hover:bg-blue-500 shadow-2xl shadow-blue-600/20 transition-all transform hover:-translate-y-1">
                Buy Now
              </button>
            </div>
          </div>

          {/* Issues Section */}
          {/* {data.categoryScores.some((cat) => cat.issues.length > 0) && (
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Issues Found</h2>
              <div className="space-y-3">
                {data.categoryScores
                  .filter((cat) => cat.issues.length > 0)
                  .map((cat, catIdx) => (
                    <div key={catIdx}>
                      <button
                        onClick={() =>
                          setExpandedIssue(expandedIssue === catIdx ? null : catIdx)
                        }
                        className="w-full text-left accordion-item flex items-start gap-3 p-4 rounded-lg border border-slate-200 hover:border-slate-300 bg-white hover:bg-slate-50 transition-all duration-200"
                      >
                        <AlertTriangle
                          size={20}
                          className="text-amber-600 flex-shrink-0 mt-0.5"
                        />
                        <div className="flex-1">
                          <p className="font-semibold text-slate-900">
                            {cat.category}
                          </p>
                          <p className="text-sm text-slate-600">
                            {cat.issues.length} issue{cat.issues.length !== 1 ? "s" : ""}{" "}
                            found
                          </p>
                        </div>
                        <span
                          className={`transform transition-transform duration-300 text-slate-400 ${
                            expandedIssue === catIdx ? "rotate-180" : ""
                          }`}
                        >
                          ▼
                        </span>
                      </button>

                      {expandedIssue === catIdx && (
                        <div className="mt-2 ml-4 space-y-2 animate-slide-in-left">
                          {cat.issues.map((issue, issueIdx) => (
                            <div
                              key={issueIdx}
                              className="flex gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg animate-fade-in"
                              style={{ animationDelay: `${issueIdx * 50}ms` }}
                            >
                              <div className="flex-shrink-0 mt-0.5">
                                <div className="w-2 h-2 rounded-full bg-amber-600"></div>
                              </div>
                              <p className="text-sm text-slate-700">{issue}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )} */}

          {/* Recommendations Section */}
          {/* {data.recommendations.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">
                <CheckCircle size={28} className="inline mr-2 text-emerald-600" />
                Recommendations
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {data.recommendations.map((rec, idx) => (
                  <div
                    key={idx}
                    className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg animate-slide-in-left"
                    style={{ animationDelay: `${idx * 50}ms` }}
                  >
                    <div className="flex gap-3">
                      <CheckCircle size={20} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-slate-700">{rec}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )} */}

          {/* Export Button */}
          {/* <div className="flex gap-3 pt-4">
            <button className="btn-primary btn-md">
              <Download size={16} className="mr-2" />
              Export Report
            </button>
            <button className="btn-secondary btn-md">Share Results</button>
          </div> */}
        </div>
      )}

      {/* Empty State */}
      {!data && !loading && !error && (
        <div className="text-center py-12">
          <div className="mb-4">
            <Search size={48} className="mx-auto text-slate-300" />
          </div>
          <p className="text-slate-600 text-lg mb-2">
            Enter a URL and click "Run Diagnostics" to analyze a website
          </p>
          <p className="text-slate-500 text-sm">
            We'll check performance, SEO, security, and other important metrics
          </p>
        </div>
      )}
    </div>
  );
}